import { useRef, useState } from 'react';

import { Button, showSnackbar, Table } from 'fluxo-ui';
import { CheckIcon, DownloadIcon, TimesIcon, UploadIcon } from 'fluxo-ui/icons';

import type { Column } from 'fluxo-ui';

interface ParsedRow {
    email: string;
    name?: string;
    valid: boolean;
    error?: string;
}

interface CSVUploaderProps {
    onUpload?: (invites: Array<{ email: string; name?: string }>) => void;
    onCancel?: () => void;
}

const CSVUploader: React.FC<CSVUploaderProps> = ({ onUpload, onCancel }) => {
    const [file, setFile] = useState<File | null>(null);
    const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
    const [parsing, setParsing] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;

        if (!selectedFile.name.endsWith('.csv')) {
            showSnackbar('Please select a CSV file', 'Invalid File', { type: 'error' });
            return;
        }

        setFile(selectedFile);
        parseCSV(selectedFile);
    };

    const parseCSV = (file: File) => {
        setParsing(true);
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const text = e.target?.result as string;
                const lines = text.split('\n').filter((line) => line.trim());

                if (lines.length === 0) {
                    showSnackbar('CSV file is empty', 'Parse Error', { type: 'error' });
                    setParsing(false);
                    return;
                }

                // Parse header
                const header = lines[0]
                    .toLowerCase()
                    .split(',')
                    .map((h) => h.trim());
                const emailIndex = header.indexOf('email');
                const nameIndex = header.indexOf('name');

                if (emailIndex === -1) {
                    showSnackbar('CSV must have an "email" column', 'Parse Error', { type: 'error' });
                    setParsing(false);
                    return;
                }

                // Parse rows
                const rows: ParsedRow[] = [];
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                for (let i = 1; i < lines.length; i++) {
                    const values = lines[i].split(',').map((v) => v.trim());
                    const email = values[emailIndex];
                    const name = nameIndex !== -1 ? values[nameIndex] : undefined;

                    if (!email) {
                        rows.push({
                            email: '',
                            name,
                            valid: false,
                            error: 'Email is required',
                        });
                        continue;
                    }

                    if (!emailRegex.test(email)) {
                        rows.push({
                            email,
                            name,
                            valid: false,
                            error: 'Invalid email format',
                        });
                        continue;
                    }

                    // Check for duplicates
                    if (rows.some((r) => r.email === email)) {
                        rows.push({
                            email,
                            name,
                            valid: false,
                            error: 'Duplicate email',
                        });
                        continue;
                    }

                    rows.push({
                        email,
                        name,
                        valid: true,
                    });
                }

                setParsedData(rows);
                const validCount = rows.filter((r) => r.valid).length;
                const invalidCount = rows.length - validCount;

                if (invalidCount > 0) {
                    showSnackbar(`Parsed ${validCount} valid emails, ${invalidCount} invalid`, 'Parse Complete', { type: 'warning' });
                } else {
                    showSnackbar(`Parsed ${validCount} valid emails`, 'Success', { type: 'success' });
                }
            } catch (error: any) {
                showSnackbar(error.message || 'Failed to parse CSV', 'Parse Error', { type: 'error' });
            } finally {
                setParsing(false);
            }
        };

        reader.onerror = () => {
            showSnackbar('Failed to read file', 'File Error', { type: 'error' });
            setParsing(false);
        };

        reader.readAsText(file);
    };

    const handleUpload = () => {
        const validInvites = parsedData.filter((row) => row.valid).map((row) => ({ email: row.email, name: row.name }));

        if (validInvites.length === 0) {
            showSnackbar('No valid emails to upload', 'Validation Error', { type: 'error' });
            return;
        }

        onUpload?.(validInvites);
    };

    const handleDownloadTemplate = () => {
        const template = 'email,name\nuser1@example.com,John Doe\nuser2@example.com,Jane Smith';
        const blob = new Blob([template], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'invite-template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSnackbar('Template downloaded', 'Success', { type: 'success' });
    };

    const columns: Column[] = [
        {
            title: 'Status',
            field: 'valid',
            template: (row: ParsedRow) => (
                <div className="flex items-center">
                    {row.valid ? (
                        <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    ) : (
                        <TimesIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                    )}
                </div>
            ),
        },
        {
            title: 'Email',
            field: 'email',
            template: (row: ParsedRow) => (
                <span className={row.valid ? 'text-slate-900 dark:text-white' : 'text-red-600 dark:text-red-400'}>{row.email || '-'}</span>
            ),
        },
        {
            title: 'Name',
            field: 'name',
            hideBelow: 'md',
            template: (row: ParsedRow) => <span className="text-slate-600 dark:text-slate-400">{row.name || '-'}</span>,
        },
        {
            title: 'Error',
            field: 'error',
            hideBelow: 'lg',
            template: (row: ParsedRow) => <span className="text-sm text-red-600 dark:text-red-400">{row.error || '-'}</span>,
        },
    ];

    const validCount = parsedData.filter((r) => r.valid).length;
    const invalidCount = parsedData.length - validCount;

    return (
        <div className="space-y-4">
            {/* Upload Section */}
            {!file ? (
                <div>
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-12 text-center cursor-pointer hover:border-teal-500 dark:hover:border-teal-400 transition-colors"
                    >
                        <UploadIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Click to upload CSV file</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">or drag and drop your file here</p>
                        <p className="text-xs text-slate-500 dark:text-slate-500">CSV file with email column (max 1MB)</p>
                    </div>
                    <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />

                    <div className="flex justify-between items-center mt-4">
                        <Button
                            label="Download Template"
                            leftIcon={DownloadIcon}
                            variant="default"
                            layout="outlined"
                            size="sm"
                            onClick={handleDownloadTemplate}
                        />
                        {onCancel && <Button label="Cancel" variant="default" layout="outlined" onClick={onCancel} />}
                    </div>
                </div>
            ) : (
                <>
                    {/* File Info */}
                    <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium text-slate-900 dark:text-white">{file.name}</div>
                                <div className="text-sm text-slate-600 dark:text-slate-400">{(file.size / 1024).toFixed(2)} KB</div>
                            </div>
                            <Button
                                label="Change File"
                                variant="default"
                                size="sm"
                                layout="outlined"
                                onClick={() => {
                                    setFile(null);
                                    setParsedData([]);
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = '';
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* Parsing Status */}
                    {parsing && (
                        <div className="text-center py-4">
                            <div className="text-slate-600 dark:text-slate-400">Parsing CSV file...</div>
                        </div>
                    )}

                    {/* Stats */}
                    {!parsing && parsedData.length > 0 && (
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                <div className="text-2xl font-bold text-slate-900 dark:text-white">{parsedData.length}</div>
                                <div className="text-xs text-slate-600 dark:text-slate-400">Total Rows</div>
                            </div>
                            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{validCount}</div>
                                <div className="text-xs text-green-600 dark:text-green-400">Valid</div>
                            </div>
                            <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{invalidCount}</div>
                                <div className="text-xs text-red-600 dark:text-red-400">Invalid</div>
                            </div>
                        </div>
                    )}

                    {/* Data Table */}
                    {!parsing && parsedData.length > 0 && (
                        <div>
                            <h4 className="font-medium text-slate-900 dark:text-white mb-3">Parsed Data</h4>
                            <div className="max-h-96 overflow-auto">
                                <Table columns={columns} rows={parsedData} totalRows={parsedData.length} pagination rowsPerPage={10} />
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    {!parsing && parsedData.length > 0 && (
                        <div className="flex justify-end gap-3">
                            {onCancel && <Button label="Cancel" variant="default" layout="outlined" onClick={onCancel} />}
                            <Button
                                label={`Upload ${validCount} Email${validCount !== 1 ? 's' : ''}`}
                                leftIcon={UploadIcon}
                                variant="primary"
                                onClick={handleUpload}
                                disabled={validCount === 0}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CSVUploader;
